import { create } from "zustand";

/**
 * eSeal / PEPPOL Anchor Store (v1)
 * 
 * Manages electronic seal generation, PEPPOL participant IDs,
 * seal signatures, confidence scores, and ledger anchoring.
 */
export interface ESealStoreState {
  documentHash: string;
  peppolParticipantID: string;
  sealTimestampISO: string;
  sealSignature: string;
  sealConfidence: number; // 0-100
  anchorLedgerHeight: number;
  validatorRegion: "EU" | "EEA" | "National";

  // Actions
  setDocumentHash: (hash: string) => void;
  setPeppolParticipantID: (id: string) => void;
  setValidatorRegion: (region: "EU" | "EEA" | "National") => void;

  // Actions
  generateSeal: () => void;
  verifySeal: () => boolean;

  // Export
  exportSealEnvelope: () => void;
}

export const useESealStore = create<ESealStoreState>((set, get) => ({
  documentHash: "",
  peppolParticipantID: "",
  sealTimestampISO: "",
  sealSignature: "",
  sealConfidence: 0,
  anchorLedgerHeight: 0,
  validatorRegion: "EU",

  setDocumentHash: (hash) => set({ documentHash: hash }),
  setPeppolParticipantID: (id) => set({ peppolParticipantID: id }),
  setValidatorRegion: (region) => set({ validatorRegion: region }),

  generateSeal: async () => {
    const state = get();
    if (!state.documentHash) return;

    const sealTimestampISO = new Date().toISOString();
    
    // WebCrypto mock: SHA-256 hash of documentHash + timestamp
    const data = `${state.documentHash}${sealTimestampISO}`;
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const sealSignature = `0x${hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")}`;

    const anchorLedgerHeight = Math.floor(Math.random() * 400000) + 100000;
    const sealConfidence = 70 + Math.floor(Math.random() * 31);

    set({
      sealTimestampISO,
      sealSignature,
      anchorLedgerHeight,
      sealConfidence,
    });
  },

  verifySeal: () => {
    const state = get();
    if (!state.sealTimestampISO || !state.sealSignature || !state.documentHash) {
      return false;
    }

    // Check timestamp drift < 48h
    const sealDate = new Date(state.sealTimestampISO);
    const now = new Date();
    const hoursDiff = (now.getTime() - sealDate.getTime()) / (1000 * 60 * 60);
    
    if (hoursDiff > 48) {
      return false;
    }

    // In production, would recompute hash and compare
    // For now, return true if all fields are present and timestamp is valid
    return state.sealSignature.length > 0 && state.sealConfidence > 0;
  },

  exportSealEnvelope: () => {
    const state = get();
    const report = {
      documentHash: state.documentHash,
      peppolParticipantID: state.peppolParticipantID,
      sealTimestampISO: state.sealTimestampISO,
      sealSignature: state.sealSignature,
      sealConfidence: state.sealConfidence,
      anchorLedgerHeight: state.anchorLedgerHeight,
      validatorRegion: state.validatorRegion,
      verified: state.verifySeal(),
    };

    const dateStr = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sealEnvelope_${dateStr}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },
}));











